/**
 * invite — the one-way seam that lets any screen (deep inside the tree, with no
 * access to the ActivityBridge) offer a Discord "invite friends" affordance.
 *
 * The real capability is `sdk.commands.openInviteDialog()` from
 * @discord/embedded-app-sdk (2.5.0): it opens Discord's own invite sheet for the
 * current voice channel. Two documented failure modes make it strictly
 * best-effort — it throws `INVALID_CHANNEL` in (G)DM contexts (no guild) and
 * `INVALID_PERMISSIONS` when the user lacks CREATE_INSTANT_INVITE — so callers
 * MUST treat a `false` return as "fall back to sharing the room code" rather than
 * an error. Nothing here ever throws.
 *
 * Activity.tsx registers the opener once the bridge is up; plain-browser surfaces
 * (`/`, `/play`, `?mock=1`) never register one, so `inviteDialogAvailable()` is
 * false and the UI shows the copy-the-code fallback instead.
 */

type InviteOpener = () => Promise<unknown>;

let opener: InviteOpener | null = null;

/** Register (or clear, with `null`) the Discord invite-dialog opener. */
export function registerInviteOpener(fn: InviteOpener | null): void {
  opener = fn;
}

/** True when a Discord invite dialog can be attempted at all. */
export function inviteDialogAvailable(): boolean {
  return opener != null;
}

/**
 * Try to open Discord's invite dialog. Resolves `true` only when Discord
 * accepted the command; `false` for "no opener registered" and for every
 * rejection (DM context, missing permission, RPC timeout). Never throws.
 */
export async function tryOpenInviteDialog(): Promise<boolean> {
  if (!opener) return false;
  try {
    await opener();
    return true;
  } catch {
    return false;
  }
}
